// src/services/api.interceptor.ts
import axios from 'axios';
import { authService } from './auth.service';
import { redirectToLogin } from '../utils/authRedirect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

// Configuration globale d'axios
axios.defaults.baseURL = API_URL;

// Routes qui utilisent un AUTRE token que le staff (espace client portail
// abonné, espace public adhérent, etc.). Pour ces routes, on ne touche PAS
// au header Authorization déjà fourni par l'appelant — sinon on remplacerait
// le token portail par le staff et le guard rejetterait avec 401.
//
// Règle générale :
//   - Toute route commençant par `/portail/` est servie par le portail
//     abonné et utilise `localStorage.portail_token` (sauf admin endpoints
//     côté backend qui sont sous `/espace-client-admin/`, hors de ce préfixe).
//   - Toute route commençant par `/public/` est anonyme.
//
// Cette logique remplace l'ancienne whitelist explicite (qui devait être
// mise à jour à chaque nouvel endpoint /portail/* — source de bugs 401).
const SKIP_AUTH_PREFIXES = ['/portail/', '/portail-abonne/', '/public/'];

function shouldSkipAuth(url?: string): boolean {
  if (!url) return false;
  return SKIP_AUTH_PREFIXES.some(prefix => url.includes(prefix));
}

// Intercepteur pour ajouter le token aux requêtes
axios.interceptors.request.use(
  (config) => {
    // Si la route est portail/public, ne pas écraser le header Authorization
    // déjà défini par l'appelant (ex: espace-client.service.ts utilise le
    // token portail stocké dans localStorage.portail_token).
    if (shouldSkipAuth(config.url)) {
      return config;
    }
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour rafraîchir le token en cas d'expiration
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Idem que la requête : on n'essaie PAS de refresh le token staff ni
    // de rediriger vers /login pour les routes portail/public — chaque
    // espace gère sa propre auth (portail_token, etc.).
    if (shouldSkipAuth(originalRequest?.url)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { access_token } = await authService.refreshToken();
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        await authService.logout();
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);